import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '@ant-design/compatible/assets/index.css';
import { Col, Form, Row, Spin, Tag, message } from 'antd';
import DF from 'DisplayFramework';
import FormInput from 'components/FormInput';
import { FormInstance } from 'antd/lib/form/Form';
import { FinContractResponse, getFinContractById } from 'services/fincontract';
import { useQuery } from 'react-query';
import UtinaCard from 'components/UtinaCard';
import Value from 'components/Value';
import DataGrid, { DataGridColDef } from 'components/DataGrid/DataGrid';
import { dateToString, indexBy, shouldUpdateCompare } from 'utils/utils2';
import ParListSelect from 'components/ParListSelect';
import TextArea from 'antd/lib/input/TextArea';
import { toCurrency } from 'utils/utils';
import InputCurrency from 'components/InputCurrency';
import _ from 'lodash';
import { calculatePrefinInterestByContract } from 'services/clientInvoice';
import moment from 'moment';
import PricingListFilteredSelect from 'components/PricingListFilteredSelect';
import { PricingResponse } from 'services/pricing';
import * as finEngine from 'services/finEngine';

export type TabColCommentProps = {
  form: FormInstance;
  objectid: number;
};

const TabLoanEndOfTerm = ({ objectid, form }: TabColCommentProps) => {
  const [isLoading, setisLoading] = useState('');

  const { data: contract, isFetching } = useQuery(
    ['getFinContractById', objectid],
    () => getFinContractById({ ctrid: objectid }).then((res) => res.data),
    {
      refetchOnWindowFocus: false,
    }
  );

  const calculatePreFin = (calculationDate?: any) => {
    setisLoading('INTERSET');
    calculatePrefinInterestByContract({
      ctrid: objectid,
      mode: 'CALCUL',
      calculationDate:
        dateToString(calculationDate) ??
        dateToString(form.getFieldValue('effectDate')) ??
        dateToString(moment(new Date(), 'YYYY-MM-DD')),
      jobid: 0,
    })
      .then((response) => {
        setisLoading('');

        form.setFieldValue(
          'intereststoinvoice',
          _.sumBy(response.list, 'calculatedamount')
        );
      })
      .catch(() => {
        setisLoading('');
      });
  };

  const calculateFees = (
    pricing?: PricingResponse,
    updatePPOFFees?: boolean
  ) => {
    finEngine
      .calculateServiceWithStep({
        servReq: {
          finSchedule: contract?.conElements[0]?.conElemSchedules ?? [],
          inDownPayment: 0,
          inFinAmount: contract?.conElements[0]?.ctefinancedamount ?? 0,
          inFollowGP: true,
          inGPDuration: contract?.conElements[0].ctegraceperiodduration ?? 0,
          inPstype: pricing?.ctecategory ?? '',
          inRoudingAmount: 2,
          inServcalculationmethod:
            pricing?.serviceResponse.servcalculationmethod ?? '',
          inServfixedamount: pricing?.serviceResponse.servfixedamount ?? 0,
          inServiceduration: pricing?.serviceResponse.servduration ?? '',
          inServpercentage: pricing?.serviceResponse.servpercentage ?? 0,
          inTaxcode: pricing?.taxcode ?? '',
          inTaxrate: 20,
          inDuration: contract?.conElements[0].cteduration ?? 0,
          inInvestAmount: contract?.conElements[0].cteinvestamount ?? 0,
          inAssetAmount: contract?.assets[0]?.assetnetamount ?? 0,
          inServperiodicity: pricing?.serviceResponse.servperiodicity ?? '1',
          inServDurationFree: pricing?.serviceResponse.servdurationdefault ?? 1,
          inStartDate: dateToString(form?.getFieldValue('effectDate')),
          inServbasis: pricing?.serviceResponse.servbasis ?? 'FLAT',

          inCalculatedamountbasis:
            pricing?.serviceResponse.calculatedamountbasis ?? 'FINANCING',

          inMarginoncalcultedamount:
            pricing?.serviceResponse.marginoncalulatedamount ?? 0,
          inAdditionalcostaftermargin:
            pricing?.serviceResponse.additionalcostaftermargin ?? 0,

          servStep: pricing?.serviceStepList ?? [],
        },
      })
      .then((res) => {
        if (res.success) {
          message.success('Service - ' + res.message);

          if (updatePPOFFees) {
            form?.setFieldsValue({
              payOffFeesAmount: res.installmentamount,
              payOffFormula: pricing?.priid,
            });
          } else {
            form?.setFieldsValue({
              feesAmount: res.installmentamount,
              feesFormula: pricing?.priid,
            });
          }
        }
      });
  };

  const reset = () => {
    if (contract) {
      let ct: FinContractResponse = contract;
      const elemFin = contract?.conElements?.find(
        (e) => e.ctetype === 'FIN' && e.ctestatus !== 'TERM'
      );

      let outstandingamount = _.sumBy(
        contract?.conElements?.filter(
          (e) => e.ctetype === 'FIN' && e.ctestatus !== 'TERM'
        ),
        'cteinvestamount'
      );

      let schedule = contract?.conElements
        ?.filter((e) => e.ctetype === 'FIN' && e.ctestatus !== 'TERM')[0]?.conElemSchedules?.filter((c) => c.ciid !== null);

      let lastinvoicedate = _.maxBy(schedule, 'ctsduedate')?.ctsduedate;

      let totalInv = 0;
      let totalReim = 0;
      contract?.conElements.forEach((e) => {
        if (e.ctestatus === 'TERM') {
          totalReim = totalReim + e.cteinvestamount - e.ctervamount;
        }

        totalInv = totalInv + _.sumBy(e?.conElemSchedules??[], 'ctsrentalamount');
      });

      form?.setFieldsValue({
        event: 'tabLoanEndOfTerm',
        ctactivationdate: ct?.ctactivationdate,
        mfusedamount: ct?.mfusedamount,
        ctenddate: ct?.ctenddate,
        ctfinancedamount: ct?.ctfinancedamount,
        lastinvoicedate: lastinvoicedate,
        totalreimbursment: totalReim,
        totalinvoicedamount: totalInv,
        outstandingamount: outstandingamount,
        currcode: contract.currcode,
        newDuration: elemFin?.cteduration,
        newRate: elemFin?.ctenominalrate,
        effectDate: elemFin?.cteenddate,
        comment: '',
      });

      calculatePreFin(contract.ctenddate);

      const elemList = contract?.conElements?.filter(
        (elem: any) =>
          elem.ctecalculationtype === 'PREFIN' && elem.ctestatus !== 'TERM'
      );

      const newElemList = elemList.map((e) => {
        return {
          ...e,
          outstandingamount: e?.cteinvestamount,
        };
      });

      form.setFieldValue('elemList', newElemList);
    }
  };

  useEffect(() => {
    reset();
  }, [contract]);

  const getName = useCallback(
    (name: string) => (record: any) => {
      const elemList = form.getFieldValue('elemList');
      const index = indexBy(elemList, 'cteid')[record.cteid];
      return ['elemList', index, name];
    },
    [form]
  );

  const columns = useMemo<DataGridColDef<any>[]>(
    () => [
      //   {
      //     dataIndex: 'flagpayoff',
      //     width: '90px',
      //     render: (val, record) => String(val ?? false),
      //   },
      // {
      //   dataIndex: 'ctecategorylabel',
      //   width: '150px',
      // },

      {
        dataIndex: 'ctedescription',
        width: '200px',
      },

      {
        dataIndex: 'outstandingamount',
        width: '150px',
        align: 'right',
        render: (val, record) => toCurrency(val, record.currcode),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form]
  );

  // rowSelection object indicates the need for row selection

  return (
    <DF.Provider pagcode="tabLoanEndOfTerm">
      <Spin spinning={isFetching}>
        <UtinaCard comcode="tabLoanEndOfTerm" level={5}>
          <Row gutter={10}>
            <FormInput col md={6} name="ctactivationdate">
              <Value />
            </FormInput>
            <FormInput col md={6} name="ctenddate">
              <Value />
            </FormInput>

            <Col md={6}>
              <FormInput name="ctfinancedamount" comcode="approuvedAmount">
                <Value formatter={(val) => toCurrency(val)} />
              </FormInput>
            </Col>
            <Col
              span={6}
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >

            <Tag color="white" style={{ borderRadius: 5,marginBottom:10 }}>
          


            <FormInput name="lastinvoicedate" noMargin>
              <Value />
            </FormInput>
            </Tag>
            </Col>

            <Col md={6}>
              <FormInput name="mfusedamount" comcode="usedAmount">
                <Value formatter={(val) => toCurrency(val)} />
              </FormInput>
            </Col>

            <FormInput col md={6} name="totalreimbursment">
              <Value formatter={(val) => toCurrency(val)} />
            </FormInput>

            <FormInput col md={6} name="totalinvoicedamount">
              <Value formatter={(val) => toCurrency(val)} />
            </FormInput>

            <Col
              span={6}
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Tag color="white" style={{ borderRadius: 5 }}>
                <FormInput
                  name="effectDate"
                  noMargin
                  rules={[{ required: true }]}
                >
                  <Value />
                </FormInput>
              </Tag>
            </Col>
          </Row>

          <Row gutter={10}>
            <Col span={18}>
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  return (
                    <DataGrid
                      scroll={{ x: 1000, y: 900 }}
                      columns={columns}
                      rowKey={(element, index) => element.cteid}
                      dataSource={getFieldValue('elemList')}
                      pagination={false}
                      isLoading={isLoading === 'POFF'}
                    />
                  );
                }}
              </Form.Item>
            </Col>
            <Col
              span={6}
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Tag color="white" style={{ marginTop: 10, borderRadius: 5 }}>
                <FormInput name="outstandingamount">
                  <Value formatter={(val) => toCurrency(val)} />
                </FormInput>
              </Tag>

              <Tag color="white" style={{ marginTop: 10, borderRadius: 5 }}>
                <Spin spinning={isLoading === 'INTERSET'} tip="Calculation...">
                  <FormInput name="intereststoinvoice" noMargin>
                    <Value formatter={(val) => toCurrency(val)} />
                  </FormInput>
                </Spin>
              </Tag>
            </Col>
          </Row>
        </UtinaCard>
        <Row>
          <UtinaCard
            level={5}
            comcode="FeesFormula"
            styleCard={{
              color: '#000',
              paddingTop: 20,
              paddingBottom: 25,
            }}
            lg={5}
          >
            <Row>
              <FormInput
                col
                xs={24}
                name="feesFormula"
                //rules={[{ required: true }]}
              >
                <PricingListFilteredSelect
                  //disabled={!getFieldValue('offerid')}
                  allowClear
                  params={{
                    offerid: contract?.offerid ?? 0,
                    ctetype: 'MODFEES', // Modifictaion fees
                    prcode: contract?.prcode ?? '',
                    entity: '',
                    entityid: 0,
                    tpid: contract?.tpidclient ?? 0,
                  }}
                  onClear={() => {
                    form?.setFieldValue('feesAmount', 0);
                  }}
                  onChange={(e) => calculateFees(e)}
                />
              </FormInput>
            </Row>
            <Row>
              <FormInput name="feesAmount" shouldUpdate>
                <InputCurrency
                  currcode={
                    contract?.currcode ?? localStorage.getItem('currcode') ?? ''
                  }
                  defaultValue={0}
                />
              </FormInput>
            </Row>
          </UtinaCard>

          <UtinaCard
            level={5}
            comcode="targetPhaseStatus"
            styleCard={{
              color: '#000',
              paddingTop: 20,
              paddingBottom: 25,
            }}
            lg={5}
          >
            <Row>
              <FormInput col xs={24} name="targetPhase" labelAlign="left">
                <ParListSelect parcode="CTENDPHASE" />
              </FormInput>
              <Form.Item
                shouldUpdate={shouldUpdateCompare('targetPhase')}
                noStyle
              >
                {({ getFieldValue }) => (
                  <FormInput col xs={24} name="targetStatus" labelAlign="left">
                    <ParListSelect
                      parcode="CTENDSTAT"
                      parvalueparent={getFieldValue('targetPhase')}
                    ></ParListSelect>
                  </FormInput>
                )}
              </Form.Item>
            </Row>
          </UtinaCard>

          <UtinaCard
            level={5}
            comcode="reason&&comment"
            styleCard={{
              color: '#000',
              paddingTop: 20,
              paddingBottom: 25,
            }}
            lg={13}
          >
            <Row gutter={10}>
              <FormInput col md={8} name="reason">
                <ParListSelect parcode="LPREIMREAS" />
              </FormInput>
              <FormInput col md={16} name="comment">
                <TextArea rows={5} />
              </FormInput>
            </Row>
          </UtinaCard>
        </Row>
      </Spin>
    </DF.Provider>
  );
};
export default TabLoanEndOfTerm;
